/// <reference path="../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />
import * as AudioBackup from "./interfaces/IAudioBackup";
import * as AudioFormat from "./interfaces/IAudioFormat";
import { CreateAndInitializeAudioFormat } from "./audioBackup/AudioFormatFactory";
import { ITranscription } from "./interfaces/ITranscriptionManager";
import { LogEvents } from "../utils/LogEvents";
import { LiveRecordingKeys } from "./telemetry/LiveRecordingKeys";
import { LogError } from "../utils/ErrorTelemetry";
import { LogEventNames } from "./interfaces/ITelemetryLogger";
import { DurationMeasureType } from "./interfaces/ILogEvent";
import { IRealTimeTranscriptionConnection } from "./interfaces/IRealTimeTranscriptionConnection";

export class LiveRecordingSession {
    private realTimeConnection: IRealTimeTranscriptionConnection;
    private audioBackup: AudioBackup.IAudioBackup;
    private audioFormat: AudioFormat.IAudioFormat | null;
    private recordedChunks: ArrayBuffer[] = [];
    private isAudioBackupEnabled: boolean;

    constructor(realTimeTranscriptionConnection: IRealTimeTranscriptionConnection, 
        onError: (error: Error) => void,
        audioBackup: AudioBackup.IAudioBackup
    ) {
        this.audioBackup = audioBackup;
        this.isAudioBackupEnabled = audioBackup.IsInitializedSuccessfully();
        this.audioFormat = null;
        this.realTimeConnection = realTimeTranscriptionConnection;
        this.realTimeConnection.SetErrorCallback(onError);
        this.realTimeConnection.SetAudioBufferCallback((buffer: AudioBuffer) => this.OnAudioBufferCallback(buffer));
    }

    public async Initialize(): Promise<void> {
        LogEvents.LiveRecording.BeginDurationMeasure(DurationMeasureType.Normal, LiveRecordingKeys.ServiceInitializationDuration);
        await this.realTimeConnection.InitializeConnection();
        LogEvents.LiveRecording.EndDurationMeasure(LiveRecordingKeys.ServiceInitializationDuration);
    }

    public async StartMicrophone(): Promise<void> {
        return this.realTimeConnection.StartMicrophone();
    }

    public async Pause(): Promise<void> {
        return this.realTimeConnection.PauseConnection();
    }

    public async Resume(): Promise<void> {
        return this.realTimeConnection.ResumeConnection();
    }

    public async GetRecording(): Promise<AudioFormat.IAudioFile> {
        return this.audioFormat.EncodeFileUsingChunks(TranscriptionStrings.FileNameToUploadAudioFiles, this.recordedChunks);
    }

    public async GetTranscript(): Promise<ITranscription> {
        return this.realTimeConnection.EndSessionAndGetFinalTranscript();
    }

    public async Close(): Promise<void> {
        LogEvents.WavFormat.End();
        this.audioFormat = null;
        await this.realTimeConnection.PauseConnection();
        await this.audioBackup.Close();

        return this.realTimeConnection.CloseConnection();
    }

    public ClearBackup() {
        return this.audioBackup.ClearBackup();
    }

    private async EncodeChunk(buffer: AudioBuffer) {
        if (this.audioFormat == null) {
            const formatProps: AudioFormat.IFormatProperties = {
                format: AudioFormat.SupportedFormats.wav,
                bitsPerSample: 16,
                sampleRateHz: buffer.sampleRate,
                countOfChannels: buffer.numberOfChannels,
                compressionPreferredKbps: 48
            };

            LogEvents.WavFormat.Start();
            this.audioFormat = CreateAndInitializeAudioFormat(formatProps);

            if (this.isAudioBackupEnabled) {
                try {
                    await this.audioBackup.BackupFormatProperties(formatProps);
                } catch (error) {
                    // If audio backup failed, disable audio backup
                    LogError(LogEventNames.IndexeddbBackup, "Audio backup failed: " + error.message);
                    this.isAudioBackupEnabled = false;
                }
            }
        }

        return this.audioFormat.EncodeChunk(buffer.getChannelData(0));
    }

    private async OnAudioBufferCallback(buffer: AudioBuffer) {
        LogEvents.LiveRecording.IncrementCount(LiveRecordingKeys.AudioChunksReceived);

        const encodedChunk = await this.EncodeChunk(buffer);
        this.recordedChunks.push(encodedChunk);

        if (!this.isAudioBackupEnabled)
            return;

        try {
            await this.audioBackup.BackupChunk(encodedChunk);
        } catch (error) {
            // If audio backup failed, disable audio backup
            LogError(LogEventNames.IndexeddbBackup, "Audio backup failed with error: " + error.message);
            this.isAudioBackupEnabled = false;
        }
    }
  };